import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../utils/translations';
import { Target, Star, Heart, CheckCircle2, BookOpen, HeartPulse, TrendingUp, Landmark, Rocket, Palette, Sparkles } from 'lucide-react';

const CompanyOverviewSection = () => {
  const { language } = useLanguage();
  const t = translations[language];
  const o = t.overview;

  const pillars = [
    { icon: Target, ...o.vision, accent: 'from-navy-700 to-blue-700' },
    { icon: Star, ...o.strengths, accent: 'from-gold-600 to-gold-700' },
    { icon: Heart, ...o.culture, accent: 'from-blue-700 to-navy-700' }
  ];

  const circleIcons = [BookOpen, HeartPulse, TrendingUp, Landmark, Rocket, Palette, Sparkles];

  return (
    <section id="overview" className="py-24 bg-white" data-testid="overview-section">
      <div className="max-w-7xl mx-auto px-6">
        {/* Section Title */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-serif font-bold text-navy-900 mb-4">{o.title}</h2>
          <div className="w-24 h-1 bg-gold-600 mx-auto"></div>
        </div>

        {/* Vision / Strengths / Culture - 3 Column Grid */}
        <div className="grid md:grid-cols-3 gap-8 mb-24">
          {pillars.map((pillar, idx) => (
            <div
              key={idx}
              data-testid={`pillar-${idx}`}
              className="bg-cream-50 border border-gray-200 rounded-2xl p-8 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              <div className={`inline-flex p-4 bg-gradient-to-br ${pillar.accent} rounded-xl mb-6`}>
                <pillar.icon className="text-white" size={28} />
              </div>
              <h3 className="text-2xl font-bold text-navy-900 mb-6">{pillar.title}</h3>
              <ul className="space-y-4">
                {pillar.items.map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <CheckCircle2 className="text-gold-600 flex-shrink-0 mt-1" size={20} />
                    <span className="text-gray-700 leading-relaxed">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Platform / 7 Circles */}
        <div className="bg-gradient-to-br from-navy-900 via-navy-800 to-blue-900 rounded-3xl p-8 md:p-16 text-white shadow-2xl">
          <div className="text-center mb-12">
            <h3 className="text-3xl md:text-4xl font-serif font-bold mb-4">{o.platform.title}</h3>
            <p className="text-lg text-gold-400 max-w-3xl mx-auto leading-relaxed">{o.platform.intro}</p>
          </div>

          {/* Objectives */}
          <div className="max-w-4xl mx-auto">
            <h4 className="text-2xl font-bold text-gold-400 mb-6 text-center">{o.platform.objectivesTitle}</h4>
            <div className="grid md:grid-cols-3 gap-6">
              {o.platform.objectives.map((obj, i) => (
                <div key={i} className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6">
                  <div className="text-4xl font-bold text-gold-400 mb-3">0{i + 1}</div>
                  <p className="text-blue-100 leading-relaxed">{obj}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CompanyOverviewSection;
